import { createMarkerLayer } from "@/helper/map-utils";
import { UrbanFeatureType } from "@/types/gql/graphql";
import { TileLayer } from "@deck.gl/geo-layers";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UrbanFeature, UrbanFeatureMode, UrbanFeatureModeType } from "../urban-feature.types";
import { CityContext } from "@/contexts/city-context-provider";
import { IconStation, MapType, mapUrlByType } from "@/types";
import { ICON_MAPPING, UrbanFeatureModalState } from "../urban-feature.const";
import { Position } from "geojson";
import { ViewState } from "react-map-gl/mapbox";
import { useQueryParamByViewPort } from "@/hooks/use-set-query-params";
import { useQueryUrbanFeature } from "./use-query-urban-feature.hook";
import { UrbanFeatureTooltipProps } from "../components/urban-feature-tooltip";
import { BitmapLayer, GeoJsonLayer, IconLayer } from "@deck.gl/layers";

export const useUrbanFeaturePoint = ({
  urbanFeatureMode,
  selectedUrbanTypes,
}: {
  urbanFeatureMode: UrbanFeatureMode;
  selectedUrbanTypes: UrbanFeatureType[];
}) => {
  const { currentCity, viewport, setViewport } = useContext(CityContext);
  const [mapType, setMapType] = useState<MapType>(MapType.MAPBOX);
  const [urbanFeaturePointModalState, setUrbanFeaturePointModalState] = useState<keyof typeof UrbanFeatureModalState>(
    UrbanFeatureModalState.DEACTIVATE,
  );

  const [urbanFeaturePointDetail, setUrbanFeaturePointDetail] = useState<UrbanFeature | null>();

  const currentPosition = useRef<Position>([]);
  const preZooming = useRef<ViewState | null>(null);
  const setQueryParamByViewport = useQueryParamByViewPort();

  const { urbanFeatureList, createUrbanFeature, updateUrbanFeature, removeUrbanFeature } = useQueryUrbanFeature({
    types: [UrbanFeatureType.TrafficLight],
  });

  const [hoverUrbanFeaturePointInfo, setHoverUrbanFeaturePointInfo] = useState<UrbanFeatureTooltipProps | null>(null);

  const getUrbanFeatures = useCallback(
    (viewState: ViewState) => {
      if (!currentCity || !viewState) return;
      setQueryParamByViewport({ viewState, viewport, cityID: currentCity.id });
    },
    [currentCity, setQueryParamByViewport, viewport],
  );

  useEffect(() => {
    if (!currentCity) {
      return;
    }
    getUrbanFeatures(viewport);
  }, [viewport, currentCity, getUrbanFeatures]);

  const trafficLightData = useMemo(() => {
    const trafficLightList: (UrbanFeature & { coordinates: Position })[] = [];
    urbanFeatureList.forEach((urbanFeature) => {
      trafficLightList.push({
        ...urbanFeature,
        coordinates: urbanFeature.geometry.coordinates,
      } as UrbanFeature & { coordinates: Position });
    });
    return trafficLightList;
  }, [urbanFeatureList]);

  const urbanFeaturePointLayer = new IconLayer({
    id: "traffic-light-icon-layer",
    data: trafficLightData,
    pickable: [UrbanFeatureModeType.EditTrafficLight, UrbanFeatureModeType.View].includes(urbanFeatureMode),
    iconAtlas: "images/traffic-light.png",
    iconMapping: ICON_MAPPING,
    getIcon: () => "marker",
    sizeScale: 15,
    getPosition: (d: IconStation) => d.coordinates,
    getSize: () => 3,
    onClick: (info) => {
      if (urbanFeatureMode !== UrbanFeatureModeType.EditTrafficLight) {
        return;
      }
      const { coordinates, ...rest } = info.object;
      rest["geometry"] = { type: "Point", coordinates };
      setUrbanFeaturePointDetail(rest);
    },
    onHover: (info) => {
      if (urbanFeatureMode !== UrbanFeatureModeType.View) {
        return;
      }
      if (info.object) {
        if (
          !hoverUrbanFeaturePointInfo ||
          (hoverUrbanFeaturePointInfo?.urbanFeature && info.object.id !== hoverUrbanFeaturePointInfo?.urbanFeature.id)
        ) {
          setHoverUrbanFeaturePointInfo({
            left: info.x,
            top: info.y,
            urbanFeature: info.object,
          });
        }
      } else if (!info.object) {
        setHoverUrbanFeaturePointInfo(null);
      }
    },
    visible: selectedUrbanTypes.length === 0 || selectedUrbanTypes.includes(UrbanFeatureType.TrafficLight),
    updateTriggers: {
      onHover: [urbanFeatureMode],
      onClick: [urbanFeatureMode],
    },
  });

  const markerLayer = useMemo(() => {
    if (!urbanFeaturePointDetail?.geometry?.coordinates?.length || urbanFeaturePointDetail?.geometry.type !== "Point") {
      return null;
    }
    return createMarkerLayer({
      coordinates: [urbanFeaturePointDetail.geometry.coordinates[0], urbanFeaturePointDetail.geometry.coordinates[1]],
    });
  }, [urbanFeaturePointDetail]);

  const currentCityLayer = new GeoJsonLayer({
    id: "current-city",
    data: currentCity?.boundaries,
    filled: false,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 4,
  });

  const tileLayer = new TileLayer({
    id: "sat-other-map",
    data: mapUrlByType[mapType],

    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,

    renderSubLayers: (props) => {
      const { boundingBox } = props.tile;

      return new BitmapLayer(props, {
        data: undefined,
        image: props.data,
        bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
      });
    },
    visible: mapType !== MapType.MAPBOX,
  });

  const urbanFeatureMapLayers = [tileLayer, urbanFeaturePointLayer, currentCityLayer, markerLayer];

  const handleClose = () => {
    setUrbanFeaturePointModalState(UrbanFeatureModalState.DEACTIVATE);
    setUrbanFeaturePointDetail(null);
  };

  const saveUrbanFeaturePoint = async (input: UrbanFeature) => {
    if (!currentCity?.id) {
      handleClose();
      return;
    }
    const newUrbanFeature: UrbanFeature = {
      name: input.name,
      address: input.address,
      geometry: input.geometry,
      cityId: currentCity.id,
      type: input.type,
    };
    if (input.id) {
      await updateUrbanFeature({ variables: { updateUrbanFeatureInput: { ...newUrbanFeature, id: input.id } } });
    } else {
      await createUrbanFeature({ variables: { createUrbanFeaturesInput: newUrbanFeature } });
    }
    handleClose();
  };
  const handleRemoveUrbanFeature = async () => {
    if (urbanFeaturePointDetail?.id) {
      await removeUrbanFeature({ variables: { urbanFeatureId: urbanFeaturePointDetail.id } });
    }
    setUrbanFeaturePointDetail(null);
  };
  return {
    urbanFeatureMapLayers,
    currentPosition,
    getUrbanFeatures,
    preZooming,
    setUrbanFeaturePointDetail,
    viewport,
    setViewport,
    urbanFeaturePointModalState,
    setUrbanFeaturePointModalState,
    urbanFeaturePointDetail,
    saveUrbanFeaturePoint,
    hoverUrbanFeaturePointInfo,
    handleClose,
    handleRemoveUrbanFeature,
    mapType,
    setMapType,
  };
};
